import type { DashboardCardKey } from '../../../../../../common/dashboard-cards.js'
import type { NextEventPayload } from '../../../../../../server/controllers/portal/dashboard-card-payloads.js'

/**
 * Conditional-visibility predicates for cards that only appear when relevant
 * (the mandatory "black box" cards). Cards absent from this map are always
 * active. Pure (type-only imports) so it's unit-testable without pulling in the
 * card components' CSS.
 */
export const CARD_IS_ACTIVE: Partial<Record<DashboardCardKey, (data: unknown) => boolean>> = {
    next_internal_event: (data) => (data as NextEventPayload).event !== null,
}

/**
 * Whether a card should be shown right now.
 *
 * Cards with a conditional-visibility predicate (the mandatory "black box"
 * cards) stay hidden while their payload is still loading (`undefined`) — they
 * only appear once the data confirms they're relevant. This avoids the flash
 * where they'd render a skeleton and then disappear when the payload resolves
 * empty. Unconditional cards keep showing their skeleton while loading. On a
 * resolved error every card stays visible so it can show its retry affordance.
 */
export const isCardActive = (key: string, data: unknown): boolean => {
    const predicate = CARD_IS_ACTIVE[key as DashboardCardKey]
    if (data === undefined) return predicate === undefined
    if (data !== null && typeof data === 'object' && 'error' in data) return true
    return predicate ? predicate(data) : true
}
